import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCurrentUser,
  getUserStreak,
  logout as apiLogout,
  deleteMyAccountHard, // <-- optional (for account deletion flow)
} from '../api/userApi';
import { normalizeUserStreak } from '../utils/streak';
import {
  clearExplicitLogout,
  markExplicitlyLoggedOut,
} from '../utils/authSession';
import { clearPostAuthRedirect } from '../utils/authRedirect';

// src/store/userSlice.js (or wherever the thunk lives)
export const fetchCurrentUser = createAsyncThunk(
  'user/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const payload = await getCurrentUser(); // { user, billing }
      if (payload) clearExplicitLogout();
      else markExplicitlyLoggedOut();
      return payload || null;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        markExplicitlyLoggedOut();
        return null;
      }
      return rejectWithValue(err?.response?.data?.error || 'Failed to fetch user');
    }
  }
);

export const fetchUserStreak = createAsyncThunk(
  'user/fetchUserStreak',
  async (_, { rejectWithValue }) => {
    try {
      return normalizeUserStreak(await getUserStreak());
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || 'Failed to fetch streak');
    }
  },
  {
    condition: (_, { getState }) => !getState().user.streakLoading,
  }
);


export const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (_, { rejectWithValue }) => {
    markExplicitlyLoggedOut();
    clearPostAuthRedirect();
    try {
      await apiLogout();        // server clears cookies / tokens
      return null;              // reducer will null the user state
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || 'Logout failed');
    }
  }
);

// OPTIONAL: if you want a delete-account thunk that also clears user state
export const deleteAccount = createAsyncThunk(
  'user/deleteAccount',
  async (input = {}, { rejectWithValue }) => {
    try {
      await deleteMyAccountHard(input);  // hard-delete on server
      try { await apiLogout(); } catch {}
      return null;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { error: 'Delete account failed' });
    }
  }
);
const normalizeUser = (payload) => {
  if (!payload) return null;

  const u = payload.user || payload;
  const planName =
    payload.planName ||
    payload.billing?.plan?.name ||
    payload.billing?.plan?.code ||
    u.planName ||
    u.plan ||
    'Starter';

  return {
    ...u,
    firstName: u.firstName ?? u.first_name ?? '',
    lastName:  u.lastName  ?? u.last_name  ?? '',
    createdAt: u.createdAt ?? u.created_at ?? '',

    // Billing bundle
    billing: payload.billing || null,
    entitlements: payload.entitlements || payload.billing?.entitlements || null,

    // Optional: convenience fields for the user profile
    planName,
    planLabel: planName,
  };
};

const clearAuthenticatedState = (state, { loading = false, bootstrapped = true } = {}) => {
  state.data = null;
  state.loading = loading;
  state.error = null;
  state.bootstrapped = bootstrapped;
  state.currentUserRequestId = null;
  state.streak = null;
  state.streakLoading = false;
  state.streakError = null;
};

const userSlice = createSlice({
  name: 'user',
  initialState: {
    data: null,
    loading: false,
    error: null,
    bootstrapped: false,
    streak: null,
    streakLoading: false,
    streakError: null,
    currentUserRequestId: null,
  },
  reducers: {
    setUser(state, action) {
      state.data = normalizeUser(action.payload);
      state.loading = false;
      state.error = null;
      state.bootstrapped = true;
      state.currentUserRequestId = null;
    },
    resetUser(state) {
      clearAuthenticatedState(state);
    },
    invalidateUserSession(state) {
      clearAuthenticatedState(state, { loading: true, bootstrapped: false });
    },
    setEntitlements(state, action) {
      if (!state.data) return;
      state.data.entitlements = action.payload || null;
      if (state.data.billing) {
        state.data.billing.entitlements = action.payload || null;
      }
    },
    setUserStreak(state, action) {
      state.streak = normalizeUserStreak(action.payload);
      state.streakError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentUserRequestId = action.meta.requestId;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        if (state.currentUserRequestId !== action.meta.requestId) return;
        state.currentUserRequestId = null;
        state.loading = false;
        state.data = normalizeUser(action.payload);
        if (!state.data) {
          state.streak = null;
          state.streakLoading = false;
          state.streakError = null;
        }
        state.bootstrapped = true;
      })
      
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        if (state.currentUserRequestId !== action.meta.requestId) return;
        state.currentUserRequestId = null;
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user';
        state.bootstrapped = true;
      })
      // logout
      .addCase(logoutUser.pending, (s) => {
        clearAuthenticatedState(s, { loading: true });
      })
      .addCase(logoutUser.fulfilled, (s) => {
        clearAuthenticatedState(s);
      })
      .addCase(logoutUser.rejected, (s, a) => { s.loading = false; s.error = a.payload || 'Failed'; })

      // OPTIONAL: account delete
      .addCase(deleteAccount.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(deleteAccount.fulfilled, (s) => {
        clearAuthenticatedState(s);
      })
      .addCase(deleteAccount.rejected, (s, a) => { s.loading = false; s.error = a.payload || 'Failed'; })
      .addCase(fetchUserStreak.pending, (s) => {
        s.streakLoading = true;
        s.streakError = null;
      })
      .addCase(fetchUserStreak.fulfilled, (s, a) => {
        s.streakLoading = false;
        s.streak = a.payload;
      })
      .addCase(fetchUserStreak.rejected, (s, a) => {
        s.streakLoading = false;
        s.streakError = a.payload || 'Failed to fetch streak';
      });
  }
});

export const {
  setUser,
  resetUser,
  invalidateUserSession,
  setEntitlements,
  setUserStreak,
} = userSlice.actions;
export default userSlice.reducer;

// handy selectors
export const selectUser = (state) => state.user.data;
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.error;
export const selectUserStreak = (state) => state.user.streak;
